/**
 * v3 — serialized background menu refresh (one in-flight fetch at a time).
 * Coalesces rapid open/close on the same date; popup-close refreshes are debounced.
 */
import type { QueryClient } from '@tanstack/react-query';
import { fetchMenu } from './api';
import { MENU_CACHE_GC_MS, MENU_CACHE_STALE_MS } from './hooks';
import { menuKeys } from './keys';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';

const POPUP_CLOSE_DEBOUNCE_MS = 350;

type QueueItem = {
  customer_id: number;
  date_ascii: string;
  is_double_box: boolean;
  force: boolean;
};

let active_drain: Promise<void> | null = null;
const pending_queue: QueueItem[] = [];
const debounce_timers = new Map<string, ReturnType<typeof setTimeout>>();
const debounce_pending = new Map<string, QueueItem>();

function itemKey(item: Pick<QueueItem, 'customer_id' | 'date_ascii' | 'is_double_box'>): string {
  return `${item.customer_id}|${item.date_ascii}|${item.is_double_box ? 1 : 0}`;
}

async function fetchMenuIntoCache(query_client: QueryClient, item: QueueItem): Promise<void> {
  const { customer_id, date_ascii, is_double_box, force } = item;
  await query_client.fetchQuery({
    queryKey: menuKeys.day(customer_id, date_ascii, is_double_box),
    queryFn: () => fetchMenu(customer_id, date_ascii, is_double_box),
    staleTime: force ? 0 : MENU_CACHE_STALE_MS,
    gcTime: MENU_CACHE_GC_MS,
  });
}

async function drainQueue(query_client: QueryClient): Promise<void> {
  while (pending_queue.length > 0) {
    const item = pending_queue.shift()!;
    try {
      await fetchMenuIntoCache(query_client, item);
    } catch {
      // Background refresh — failures are non-blocking.
    }
  }
}

function kickDrain(query_client: QueryClient): void {
  if (active_drain) {
    return;
  }
  active_drain = drainQueue(query_client).finally(() => {
    active_drain = null;
    if (pending_queue.length > 0) {
      kickDrain(query_client);
    }
  });
}

function enqueueMenuRefresh(query_client: QueryClient, item: QueueItem): void {
  const key = itemKey(item);
  for (let i = pending_queue.length - 1; i >= 0; i--) {
    if (itemKey(pending_queue[i]!) === key) {
      pending_queue.splice(i, 1);
    }
  }
  pending_queue.push(item);
  kickDrain(query_client);
}

/** Debounced force refresh after a calendar date popup closes (rating / selection / display). */
export function scheduleV3MenuRefreshAfterPopupClose(
  query_client: QueryClient,
  customer_id: number,
  date: string,
  is_double_box = false,
): void {
  if (!customer_id || !date) {
    return;
  }
  const date_ascii = ensureAsciiDateString(date) || date;
  const key = itemKey({ customer_id, date_ascii, is_double_box });

  const existing_timer = debounce_timers.get(key);
  if (existing_timer) {
    clearTimeout(existing_timer);
  }

  debounce_pending.set(key, { customer_id, date_ascii, is_double_box, force: true });

  debounce_timers.set(
    key,
    setTimeout(() => {
      debounce_timers.delete(key);
      const pending = debounce_pending.get(key);
      debounce_pending.delete(key);
      if (pending) {
        enqueueMenuRefresh(query_client, pending);
      }
    }, POPUP_CLOSE_DEBOUNCE_MS),
  );
}

/** Refresh box 1 and box 2 when the delivery day is a double-box date. */
export function scheduleV3MenuRefreshAfterPopupCloseForDay(
  query_client: QueryClient,
  customer_id: number,
  date: string,
  is_double_box_day: boolean,
): void {
  scheduleV3MenuRefreshAfterPopupClose(query_client, customer_id, date, false);
  if (is_double_box_day) {
    scheduleV3MenuRefreshAfterPopupClose(query_client, customer_id, date, true);
  }
}

/** Enqueue a non-forced menu fetch (reuses cache when fresh). Shares the single-flight queue. */
export function scheduleV3MenuBackgroundPrefetch(
  query_client: QueryClient,
  customer_id: number,
  date: string,
  is_double_box = false,
): void {
  if (!customer_id || !date) {
    return;
  }
  const date_ascii = ensureAsciiDateString(date) || date;
  enqueueMenuRefresh(query_client, { customer_id, date_ascii, is_double_box, force: false });
}
